import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { store } from './store';
import { fetchSettings } from './store/settingsSlice';
import { fetchTimers, addTimer, deleteTimer, updateTimer } from './store/timersSlice';
import TimersContainer from './components/organisms/TimersContainer';
import SettingsPanel from './components/organisms/SettingsPanel';
import AboutPanel from './components/organisms/AboutPanel';
import { useSelector } from 'react-redux';
import { RootState } from './store';
import { AnimatePresence } from 'framer-motion';
import { FaCog, FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';
import { useAppDispatch } from './hooks/useAppDispatch';
import './App.css';

// 应用内容组件
const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const timers = useSelector((state: RootState) => state.timers.items);
  const darkMode = useSelector((state: RootState) => state.settings.darkMode);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const { t, i18n } = useTranslation();
  
  // 初始化数据
  useEffect(() => {
    dispatch(fetchTimers());
    dispatch(fetchSettings());
  }, [dispatch]);
  
  // 设置浏览器窗口标题
  useEffect(() => {
    document.title = t('appTitle');
  }, [t, i18n.language]);
  
  // 添加计时器
  const handleAddTimer = (timerData: any) => {
    dispatch(addTimer(timerData));
  };
  
  // 删除计时器
  const handleDeleteTimer = (id: string) => {
    dispatch(deleteTimer(id));
  };
  
  // 更新计时器
  const handleUpdateTimer = (id: string, changes: any) => {
    dispatch(updateTimer({ id, changes }));
  };
  
  // 打开设置面板
  const openSettings = () => {
    setIsSettingsOpen(true);
    setIsAboutOpen(false);
  };
  
  // 关闭设置面板
  const closeSettings = () => {
    setIsSettingsOpen(false);
  };
  
  // 打开关于面板
  const openAbout = () => {
    setIsAboutOpen(true);
    setIsSettingsOpen(false);
  };
  
  // 关闭关于面板
  const closeAbout = () => {
    setIsAboutOpen(false);
  };
  
  return (
    <div className={`App ${darkMode ? 'dark' : ''}`}>
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
        <header className="bg-white dark:bg-gray-800 shadow-md">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-bold">{t('appTitle')}</h1>
            <div className="flex items-center space-x-2">
              <button 
                onClick={openAbout}
                className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label={t('about')}
              >
                <FaQuestionCircle size={20} />
              </button>
              <button 
                onClick={openSettings}
                className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label={t('settings')}
              >
                <FaCog size={20} />
              </button>
            </div>
          </div>
        </header>
        
        {/* 网站功能简介 */}
        <div className="bg-blue-50 dark:bg-blue-900/20 py-6 border-b border-blue-100 dark:border-blue-800">
          <div className="container mx-auto px-4 text-center">
            <p className="text-blue-800 dark:text-blue-200 flex items-center justify-center">
              <FaInfoCircle className="mr-2" />
              {t('siteIntro')}
            </p>
          </div>
        </div>
        
        <main className="container mx-auto px-4 py-8">
          <TimersContainer 
            timers={timers}
            onAddTimer={handleAddTimer}
            onDeleteTimer={handleDeleteTimer}
            onUpdateTimer={handleUpdateTimer}
          />
        </main>
        
        <AnimatePresence>
          {isSettingsOpen && (
            <SettingsPanel 
              isOpen={isSettingsOpen} 
              onClose={closeSettings} 
            />
          )}
          
          {isAboutOpen && (
            <AboutPanel
              isOpen={isAboutOpen}
              onClose={closeAbout}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// 主应用组件
function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
